import type { LogEntry, GenesisConfig, Checkpoint } from '../../src/core/types.js';

export interface TokenSummary {
  id: string;
  symbol: string;
  name: string;
  description: string;
  image: string;
  creator: string;
  createdTs: number;
  mode: 'curve' | 'amm';
  priceZat: number;
  marketCapZat: string;
  progressBps: number;
  realZat: string;
  volumeZat: string;
  volume24hZat: string;
  change24h: number;
  trades: number;
  lastTradeTs: number;
  graduated: boolean;
  /** Fair launch: buys before launchEnd fill together at one price. */
  launchEnd: number;
  launchOpen: boolean;
  launchZat: string;
  launchOrders: number;
  /** Share of the supply bought in the launch minute, in basis points. */
  launchBoughtBps: number;
  creatorHoldsBps: number;
  creatorFeesZat: string;
  /** Wallet cap (3%) applies until this time; 0 if there is none. */
  capUntil: number;
  /** The anti-snipe fee is gone by this time. */
  snipeUntil: number;
}

export interface Trade {
  seq: number;
  ts: number;
  token: string;
  side: 'buy' | 'sell';
  user: string;
  zat: string;
  tokens: string;
  price: number;
}

export interface PoolData {
  vZat: string;
  vTok: string;
  realZat: string;
  realTok: string;
  reserveTok: string;
}

export interface TokenDetail {
  token: TokenSummary;
  /** Aggregates only: how concentrated the coin is, without naming anyone. */
  safety: { holders: number; top10Bps: number; largestBps: number };
  pool: PoolData;
  holders: { user: string; amount: string }[];
  trades: Trade[];
}

export interface Account {
  exists: boolean;
  zat?: string;
  /** Payments on their way to the deposit address, not credited until mined deep enough. */
  incoming?: { zat: string; confirmations: number; needed: number }[];
  nonce?: number;
  depositAddress?: string | null;
  tokens?: (TokenSummary & { balance: string })[];
  pending?: { id: number; zat: string; sendZat: string; commit: string; needsApproval: boolean }[];
  trades?: Trade[];
  /** ZEC waiting in launch-minute batches. */
  launchOrders?: { token: string; symbol: string; zat: string; fillsAt: number }[];
  /** Coins this account launched and the creator fees they paid it. */
  created?: { token: string; symbol: string; earnedZat: string }[];
}

export interface Speed {
  samples: number;
  p50Ms: number | null;
  p95Ms: number | null;
  p99Ms: number | null;
  minConfirmations: number;
  network: string;
}

export interface Info {
  genesis: GenesisConfig;
  /** The log every request must name. */
  chainId: string;
  /** Server clock (ms) when this was sent: wallets sign with it, since requests far off it are refused. */
  now: number;
  sequencer: string;
  seq: number;
  head: string;
  checkpoint: Checkpoint | null;
  stats: { users: number; tokens: number; liabilitiesZat: string; pendingWithdrawals: number; notesOut: Record<string, string> };
  beta: { autoWithdrawMaxZat: string; dailyWithdrawCapZat: string; maxUserDepositZat: string; minConfirmations: number; fastDepositMaxZat?: string };
  zecUsd?: number | null;
}

export interface NoteMint {
  seq: number;
  ts: number;
  denom: string;
  blinded: string[];
  sigs: string[];
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Where the server is: empty when the server serves this page itself; set at build time
 *  (DAZZLE_API_URL) when the pages are hosted elsewhere, e.g. on Vercel. */
declare const __API__: string;
export const API = __API__;

async function get<T>(path: string): Promise<T> {
  const r = await fetch(API + path);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(j.code ?? 'http', j.error ?? `HTTP ${r.status}`);
  return j as T;
}

export const api = {
  info: () => get<Info>('/api/info'),
  tokens: (sort = 'new', q = '') => get<{ tokens: TokenSummary[] }>(`/api/tokens?sort=${sort}&q=${encodeURIComponent(q)}`).then((r) => r.tokens),
  token: (id: string) => get<TokenDetail>(`/api/token/${encodeURIComponent(id)}`),
  candles: (id: string, interval: number) =>
    get<{ candles: { t: number; o: number; h: number; l: number; c: number; v: number }[] }>(`/api/token/${encodeURIComponent(id)}/candles?interval=${interval}`).then((r) => r.candles),
  account: (id: string) => get<Account>(`/api/account/${encodeURIComponent(id)}`),
  proof: (id: string) => get<{ checkpoint: Checkpoint; record: unknown; proof: import('../../src/core/merkle.js').MerkleProof; root: string }>(`/api/proof/${encodeURIComponent(id)}`),
  log: (from: number, limit: number) => get<{ entries: LogEntry[]; seq: number; head: string }>(`/api/log?from=${from}&limit=${limit}`),
  /** Every checkpoint (the server pages them). */
  checkpoints: async (): Promise<Checkpoint[]> => {
    const out: Checkpoint[] = [];
    for (;;) {
      const r = await get<{ total: number; checkpoints: Checkpoint[] }>(`/api/checkpoints?from=${out.length}`);
      out.push(...r.checkpoints);
      if (r.checkpoints.length === 0 || out.length >= r.total) return out;
    }
  },
  activity: () => get<{ activity: { seq: number; ts: number; kind: string; text: string }[] }>('/api/activity').then((r) => r.activity),
  speed: () => get<Speed>('/api/speed'),
  submit: async (body: unknown): Promise<LogEntry> => {
    const r = await fetch(API + '/api/submit', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new ApiError(j.code ?? 'http', j.error ?? `HTTP ${r.status}`);
    return j.receipt as LogEntry;
  },
  /** Every note mint on the platform (public, paged); the wallet finds its own locally. */
  allMints: async (): Promise<NoteMint[]> => {
    const out: NoteMint[] = [];
    for (;;) {
      const r = await get<{ total: number; mints: NoteMint[] }>(`/api/notes/mints?from=${out.length}`);
      out.push(...r.mints);
      if (r.mints.length === 0 || out.length >= r.total) return out;
    }
  },
  /** Every spent note serial (public, paged). */
  allSpent: async (): Promise<Set<string>> => {
    const out: string[] = [];
    for (;;) {
      const r = await get<{ total: number; spent: string[] }>(`/api/notes/spent?from=${out.length}`);
      out.push(...r.spent);
      if (r.spent.length === 0 || out.length >= r.total) return new Set(out);
    }
  },
  devDeposit: (user: string, zecAmount: number) =>
    fetch(API + '/dev/deposit', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ user, zec: zecAmount }) }).then((r) => r.json()),
};

// ------------------------------------------------------------------ live feed

type Listener = (e: LogEntry) => void;
const listeners = new Set<Listener>();
let ws: WebSocket | null = null;
let backoff = 500;

function connect() {
  const base = new URL(API || location.origin);
  ws = new WebSocket(`${base.protocol === 'https:' ? 'wss' : 'ws'}://${base.host}/ws`);
  ws.onmessage = (m) => {
    try {
      const msg = JSON.parse(m.data);
      if (msg.type === 'entry') for (const l of listeners) l(msg.entry);
    } catch {
      /* ignore */
    }
  };
  ws.onopen = () => (backoff = 500);
  ws.onclose = () => {
    setTimeout(connect, backoff);
    backoff = Math.min(backoff * 2, 10_000);
  };
}

export function onEntry(fn: Listener): () => void {
  if (!ws) connect();
  listeners.add(fn);
  return () => listeners.delete(fn);
}
